import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { Category, Pin, Snapshot } from '@/lib/api';
import { votePin } from '@/lib/api';
import { PODPORA, plural } from '@/lib/plural';

interface PinDetailProps {
  pin: Pin;
  /** All known categories; the card resolves the pin's own from this. */
  categories: Category[];
  onClose: () => void;
}

/**
 * Detail card for one pin.
 *
 * The only action is support. There is no downvote control, and no "report"
 * flow that would function as one — moderation happens against published
 * rules, not by crowd pressure.
 */
export function PinDetail({ pin, categories, onClose }: PinDetailProps) {
  // Resolved in the pin's own order, so the primary category reads first and
  // matches the colour of the dot the reader just clicked.
  const pinCategories = pin.categories
    .map((slug) => categories.find((c) => c.slug === slug))
    .filter((c): c is Category => c !== undefined);

  const queryClient = useQueryClient();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, [pin.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const vote = useMutation({
    mutationFn: () => votePin(pin.id),
    onSuccess: (result) => {
      // Patch the cached snapshot rather than refetching: the server has
      // already told us the authoritative count for this pin.
      queryClient.setQueryData<Snapshot>(['snapshot'], (current) => {
        if (!current) return current;
        const previous = current.pins.find((p) => p.id === pin.id)?.votes ?? 0;
        return {
          ...current,
          pins: current.pins.map((p) => (p.id === pin.id ? { ...p, votes: result.votes } : p)),
          // Keep the header total in step, otherwise the page shows a pin with
          // one vote next to a site-wide total of zero.
          counts: {
            ...current.counts,
            votes: current.counts.votes + (result.votes - previous),
          },
        };
      });
    },
  });

  const alreadyCounted = vote.data?.counted === false;

  return (
    <section
      aria-labelledby={`pin-${pin.id}-title`}
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap gap-1">
            {pinCategories.map((category) => (
              <span
                key={category.slug}
                className="inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
                style={{ backgroundColor: category.color }}
              >
                {category.name_cs}
              </span>
            ))}
          </div>
          <h2 id={`pin-${pin.id}-title`} className="mt-1.5 text-lg font-semibold text-slate-900">
            {pin.title}
          </h2>
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Zavřít detail podnětu"
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {pin.description && (
        <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{pin.description}</p>
      )}

      {/*
        A non-votable pin shows no support control and no count. Hiding the
        button but keeping "0 podpor" would read as "nobody cares about this"
        rather than "this is not the kind of record you vote on". The API
        refuses the vote either way; this is the honest presentation of it.
      */}
      {pin.votable ? (
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => vote.mutate()}
            disabled={vote.isPending || vote.isSuccess}
            className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-4 py-2 font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M10 3l7 8h-4v6H7v-6H3l7-8z" fill="currentColor" />
            </svg>
            {vote.isSuccess ? 'Podpořeno' : 'Podpořit'}
          </button>

          <p className="text-sm text-slate-600">
            <strong className="text-slate-900">{pin.votes}</strong> {plural(pin.votes, PODPORA)}
          </p>
        </div>
      ) : (
        <p className="mt-4 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
          Tento záznam se nepodporuje — je to doklad o už položeném dotazu, ne návrh k hlasování.
        </p>
      )}

      {alreadyCounted && (
        <p role="status" className="mt-2 text-sm text-slate-600">
          Tento podnět už jste dříve podpořili — započítal se jednou.
        </p>
      )}
      {vote.isError && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {(vote.error as Error).message}
        </p>
      )}

      <p className="mt-3 text-xs text-slate-500">
        Přidáno {new Date(pin.created_at).toLocaleDateString('cs-CZ')}
        {pin.source === 'import' && ' · z otevřených dat'}
      </p>
    </section>
  );
}
